import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { invoicesApi } from '../api/endpoints';
import { useAuthStore } from '../stores/auth.store';
import type { Invoice } from '@motra/shared';

export function useInvoices(shopId?: string, sessionId?: string) {
  const shop = useAuthStore((s) => s.shop);
  const activeShopId = shopId ?? shop?.id;

  return useQuery({
    queryKey: ['invoices', activeShopId, sessionId],
    queryFn: () => invoicesApi.list(activeShopId!, { session_id: sessionId }),
    enabled: !!activeShopId,
    select: (res) => res.data ?? [],
  });
}

export function useInvoice(invoiceId?: string) {
  return useQuery({
    queryKey: ['invoice', invoiceId],
    queryFn: () => invoicesApi.retrieve(invoiceId!),
    enabled: !!invoiceId,
    select: (res) => res.data,
  });
}

export function useGenerateInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<Invoice> & { shopid: string }) =>
      invoicesApi.generate(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
  });
}

export function useVoidInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ invoiceId, reason }: { invoiceId: string; reason?: string }) =>
      invoicesApi.void(invoiceId, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoice'] });
    },
  });
}
